import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Users, ChevronRight, Heart, MessageCircle, Shield, Sparkles, Clock, BookOpen, AlertTriangle, Flame } from 'lucide-react';
import axios from 'axios';
import PremiumLock from '@/components/PremiumLock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'understand', title: 'Förstå dig själv', icon: Heart, color: 'bg-pink-100 text-pink-600' },
  { id: 'communication', title: 'Kommunikation', icon: MessageCircle, color: 'bg-blue-100 text-blue-600' },
  { id: 'conflict', title: 'Konflikthantering', icon: Shield, color: 'bg-amber-100 text-amber-600' },
  { id: 'exercises', title: 'Parövningar', icon: Users, color: 'bg-purple-100 text-purple-600' },
  { id: 'advice', title: 'Relationsråd', icon: Sparkles, color: 'bg-emerald-100 text-emerald-600' },
];

export default function RelationshipsPage() {
  const navigate = useNavigate();
  const { hasAccess } = useAuth();
  
  const [topics, setTopics] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedExercise, setSelectedExercise] = useState(null);

  useEffect(() => {
    if (hasAccess) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [hasAccess]);

  const fetchData = async () => {
    try {
      const [topicsRes, exercisesRes] = await Promise.all([
        axios.get(`${API}/relationships`),
        axios.get(`${API}/couple-exercises`)
      ]);
      setTopics(topicsRes.data);
      setExercises(exercisesRes.data);
    } catch (error) {
      toast.error('Kunde inte hämta innehåll');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (selectedTopic || selectedExercise) {
      setSelectedTopic(null);
      setSelectedExercise(null);
    } else if (selectedCategory) {
      setSelectedCategory(null);
    } else {
      navigate('/category/relations');
    }
  };

  const getTopicsForCategory = (categoryId) => {
    return topics.filter(t => t.category === categoryId);
  };

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
            <Button variant="ghost" onClick={() => navigate('/category/relations')} className="rounded-full hover:bg-secondary/80">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Startsida
            </Button>
            <h1 className="font-heading text-xl font-light">Relationer</h1>
            <div className="w-24" />
          </div>
        </header>
        <PremiumLock feature="Relationer" />
      </div>
    );
  }

  // Topic detail view
  if (selectedTopic) {
    return (
      <div className="min-h-screen bg-background" data-testid="relationships-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
            <Button variant="ghost" onClick={handleBack} className="rounded-full hover:bg-secondary/80">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Tillbaka
            </Button>
            <h1 className="font-heading text-xl font-light">Relationer</h1>
            <div className="w-24" />
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
          <div className="bg-pink-50 rounded-2xl p-8">
            <Heart className="w-10 h-10 text-pink-500 mb-4" />
            <h2 className="font-heading text-2xl font-semibold mb-2">{selectedTopic.title}</h2>
            <p className="text-muted-foreground">{selectedTopic.description}</p>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-border/30 space-y-4">
            {selectedTopic.content.map((text, i) => (
              <p key={i} className="text-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
            ))}
          </div>

          <div className="bg-primary/5 rounded-2xl p-6 space-y-4">
            <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" />
              Reflektionsfrågor
            </h3>
            {selectedTopic.reflection_questions.map((q, i) => (
              <div key={i} className="flex gap-3 items-start p-4 rounded-xl bg-white">
                <span className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary shrink-0">{i + 1}</span>
                <span>{q}</span>
              </div>
            ))}
          </div>

          {/* Contact & Disclaimer */}
          
          <p className="text-xs text-muted-foreground text-center px-4">
            ⚕️ Informationen här ersätter inte professionell terapi. Sök hjälp om du befinner dig i en destruktiv relation.
          </p>
        </main>
      </div>
    );
  }

  // Exercise detail view
  if (selectedExercise) {
    return (
      <div className="min-h-screen bg-background" data-testid="relationships-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
            <Button variant="ghost" onClick={handleBack} className="rounded-full hover:bg-secondary/80">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Tillbaka
            </Button>
            <h1 className="font-heading text-xl font-light">Parövning</h1>
            <div className="w-24" />
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
          <div className="bg-purple-50 rounded-2xl p-8">
            <div className="flex items-center gap-2 text-purple-600 text-sm mb-3">
              <Clock className="w-4 h-4" />
              {selectedExercise.time}
            </div>
            <h2 className="font-heading text-2xl font-semibold mb-2">{selectedExercise.title}</h2>
            <p className="text-muted-foreground">{selectedExercise.description}</p>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-border/30 space-y-4">
            <h3 className="font-semibold">Så gör ni</h3>
            <ol className="space-y-3">
              {selectedExercise.instructions.map((step, i) => (
                <li key={i} className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-sm font-medium flex-shrink-0">
                    {i + 1}
                  </span>
                  <span className="text-muted-foreground">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="bg-amber-50 rounded-2xl p-4 border border-amber-100">
            <p className="text-amber-800 text-sm">
              💡 <strong>Tips:</strong> {selectedExercise.tips}
            </p>
          </div>

          {/* Contact & Disclaimer */}
          
          <p className="text-xs text-muted-foreground text-center px-4">
            ⚕️ Informationen här ersätter inte professionell terapi. Sök hjälp om du befinner dig i en destruktiv relation.
          </p>
        </main>
      </div>
    );
  }

  // Category view (topics or exercises list)
  if (selectedCategory) {
    const category = CATEGORIES.find(c => c.id === selectedCategory);
    const isExercises = selectedCategory === 'exercises';
    const items = isExercises ? exercises : getTopicsForCategory(selectedCategory);

    return (
      <div className="min-h-screen bg-background" data-testid="relationships-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
            <Button variant="ghost" onClick={handleBack} className="rounded-full hover:bg-secondary/80">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Tillbaka
            </Button>
            <h1 className="font-heading text-xl font-light">{category.title}</h1>
            <div className="w-24" />
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-6 py-8">
          <div className="text-center mb-8">
            <div className={`w-16 h-16 mx-auto rounded-full ${category.color} flex items-center justify-center mb-4`}>
              <category.icon className="w-7 h-7" />
            </div>
            <h2 className="font-heading text-2xl font-semibold">{category.title}</h2>
          </div>

          <div className="space-y-3">
            {items.map((item, index) => (
              <button
                key={item.id}
                onClick={() => isExercises ? setSelectedExercise(item) : setSelectedTopic(item)}
                className="w-full bg-white p-5 rounded-2xl border border-border/30 text-left hover:shadow-md transition-all group"
                data-testid={`topic-${item.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold mb-1">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                    {isExercises && (
                      <div className="flex items-center gap-2 mt-2 text-xs text-purple-600">
                        <Clock className="w-3 h-3" />
                        {item.time}
                      </div>
                    )}
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
              </button>
            ))}
          </div>
        </main>
      </div>
    );
  }

  // Main category selection view
  return (
    <div className="min-h-screen bg-background" data-testid="relationships-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button variant="ghost" onClick={() => navigate('/category/relations')} className="rounded-full hover:bg-secondary/80">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Startsida
          </Button>
          <h1 className="font-heading text-xl font-light">Relationer</h1>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary/50" />
          </div>
        ) : (
          <>
            <div className="text-center mb-10 space-y-3">
              <div className="w-16 h-16 mx-auto rounded-full bg-pink-100 flex items-center justify-center">
                <Heart className="w-7 h-7 text-pink-500" />
              </div>
              <h2 className="font-heading text-2xl font-semibold">Relationer</h2>
              <p className="text-muted-foreground max-w-lg mx-auto">
                Förstå dina relationsmönster, kommunicera bättre och bygg hälsosammare kopplingar.
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {CATEGORIES.map((category, index) => {
                const itemCount = category.id === 'exercises' 
                  ? exercises.length 
                  : getTopicsForCategory(category.id).length;
                
                return (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className="bg-white p-6 rounded-2xl border border-border/30 text-left hover:shadow-lg transition-all group"
                    data-testid={`category-${category.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 rounded-xl ${category.color} flex items-center justify-center`}>
                        <category.icon className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{category.title}</h3>
                        <p className="text-sm text-muted-foreground">{itemCount} {category.id === 'exercises' ? 'övningar' : 'ämnen'}</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Quick tips */}
            <div className="mt-10 bg-pink-50 rounded-2xl p-6 border border-pink-100">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Flame className="w-5 h-5 text-pink-500" />
                Snabbtips för starkare relationer
              </h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• Säg tre positiva saker om din partner varje dag</li>
                <li>• Fråga "Hur mår du egentligen?" och lyssna på svaret</li>
                <li>• Planera in tid för bara er två – utan telefoner</li>
                <li>• Säg förlåt snabbt och genuint när du har fel</li>
              </ul>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
